import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
  Alert,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentIcon from "@mui/icons-material/Assignment";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";

import LoginFlyout from "./LoginFlyout";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL, API_UPLOAD_URL } from "./config";

function getCampusFromHost(hostname) {
  if (!hostname) return "";

  const host = hostname.toLowerCase();

  if (host === "localhost" || host === "127.0.0.1") return "";

  const parts = host.split(".");

  if (
    parts.length >= 3 &&
    parts[parts.length - 2] === "unifloo" &&
    parts[parts.length - 1] === "com"
  ) {
    const subdomain = parts[0];
    if (subdomain === "www") return "";
    return subdomain.toUpperCase();
  }

  return "";
}

function resolveAssetUrl(path) {
  if (!path) return "";

  const value = String(path).trim();
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) return value;

  const uploadBase = (API_UPLOAD_URL || "").replace(/\/+$/, "");

  if (value.startsWith("/api/uploads/")) return `${uploadBase}${value}`;
  if (value.startsWith("api/uploads/")) return `${uploadBase}/${value}`;

  if (value.startsWith("/uploads/")) return `${uploadBase}${value}`;
  if (value.startsWith("uploads/")) return `${uploadBase}/${value}`;

  return `${uploadBase}/uploads/${value.replace(/^\/+/, "")}`;
}

function CampusLandingPage({ campusCode }) {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyAnchorEl, setApplyAnchorEl] = useState(null);
  const [guardAnchorEl, setGuardAnchorEl] = useState(null);

  const navigate = useNavigate();

  const applyOpen = Boolean(applyAnchorEl);
  const guardOpen = Boolean(guardAnchorEl);

  const handleApplyClose = () => setApplyAnchorEl(null);
  const handleGuardClose = () => setGuardAnchorEl(null);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/campus-landing/${campusCode}`
        );
        setBranding(data);
      } catch (err) {
        console.error(err);
        setBranding(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, [campusCode]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          bgcolor: "#F2F2F2",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const primary = branding?.PrimaryThemeColor || "#F07828";
  const bgImage = resolveAssetUrl(branding?.LandingBackgroundImage);

  const title =
    branding?.LandingTitle ||
    `${branding?.CampusName || campusCode} Digital Campus`;

  const subtitle =
    branding?.LandingSubtitle ||
    "Access academics, attendance, fees, approvals, student services, and campus workflows from one secure portal.";

  const headerButtonSx = {
    minWidth: { xs: 88, sm: "auto" },
    px: { xs: 1.2, sm: 2 },
    py: { xs: 0.65, sm: 0.85 },
    fontSize: { xs: 12, sm: 14 },
    fontWeight: 800,
    borderRadius: 2,
    textTransform: "none",
    whiteSpace: "nowrap",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#8EC9EA",
      }}
    >
      {bgImage && (
        <Box
          component="img"
          src={bgImage}
          alt="Campus background"
          sx={{
            position: "absolute",
            top: { xs: 0, sm: 64, md: 64 },
            left: 0,
            width: "100%",
            height: {
              xs: "100%",
              sm: "calc(100vh - 64px)",
              md: "calc(100vh - 64px)",
            },
            objectFit: "fill",
            objectPosition: "center center",
            zIndex: 0,
            display: "block",
          }}
        />
      )}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          background:
            "linear-gradient(135deg, rgba(4,27,55,0.22), rgba(25,118,210,0.08))",
        }}
      />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: 3,
          background: "rgba(6,45,86,0.36)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(255,255,255,0.16)",
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 1.25, sm: 2, md: 4 },
            py: { xs: 1, md: 1 },
            gap: { xs: 1, sm: 2 },
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <Box sx={{ flexGrow: 1, width: { xs: "100%", sm: "auto" } }}>
            <Typography
              sx={{
                color: "white",
                fontWeight: 900,
                lineHeight: 1.1,
                fontSize: { xs: 15, sm: 17, md: 20 },
                textShadow: "0 3px 14px rgba(0,0,0,0.35)",
                textAlign: { xs: "center", sm: "left" },
                wordBreak: "break-word",
              }}
            >
              {branding?.CampusName || campusCode}
            </Typography>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.78)",
                fontSize: { xs: 11, sm: 12 },
                textShadow: "0 3px 14px rgba(0,0,0,0.35)",
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              Powered by UniFLOO
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: "center", sm: "flex-end" }}
            sx={{
              width: { xs: "100%", sm: "auto" },
              flexWrap: "wrap",
              rowGap: 1,
            }}
          >
            <Button
              variant="contained"
              endIcon={<ExpandMoreIcon />}
              onClick={(e) => setApplyAnchorEl(e.currentTarget)}
              sx={{
                ...headerButtonSx,
                bgcolor: primary,
                color: "#fff",
                boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
                "&:hover": {
                  bgcolor: primary,
                  opacity: 0.92,
                },
              }}
            >
              Apply
            </Button>

            <Menu
              anchorEl={applyAnchorEl}
              open={applyOpen}
              onClose={handleApplyClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 230,
                  borderRadius: 2,
                  boxShadow: "0 14px 40px rgba(15,23,42,0.18)",
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  handleApplyClose();
                  navigate("/apply");
                }}
              >
                <ListItemIcon>
                  <AssignmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Student Application Form" />
              </MenuItem>
            </Menu>

            <Button
              variant="outlined"
              endIcon={<ExpandMoreIcon />}
              onClick={(e) => setGuardAnchorEl(e.currentTarget)}
              sx={{
                ...headerButtonSx,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.55)",
                bgcolor: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(10px)",
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: "rgba(255,255,255,0.16)",
                },
              }}
            >
              Guard
            </Button>

            <Menu
              anchorEl={guardAnchorEl}
              open={guardOpen}
              onClose={handleGuardClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 230,
                  borderRadius: 2,
                  boxShadow: "0 14px 40px rgba(15,23,42,0.18)",
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  handleGuardClose();
                  navigate("/guard");
                }}
              >
                <ListItemIcon>
                  <QrCodeScannerIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Guard Scanner" />
              </MenuItem>
              {/*
              <MenuItem
                onClick={() => {
                  handleGuardClose();
                  navigate("/guard-register");
                }}
              >
                <ListItemIcon>
                  <AppRegistrationIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Guard Register" />
              </MenuItem>
              */}
            </Menu>

            <Box
              sx={{
                "& button": {
                  minWidth: { xs: 88, sm: "auto" },
                  px: { xs: 1.2, sm: 2 },
                  py: { xs: 0.65, sm: 0.85 },
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: 800,
                  borderRadius: 2,
                  whiteSpace: "nowrap",
                },
              }}
            >
              <LoginFlyout buttonVariant="contained" />
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          pt: { xs: 18, sm: 14, md: 10 },
          pb: 4,
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12}>
            <Typography
              sx={{
                color: "#F8FBFF",
                fontWeight: 900,
                fontSize: { xs: 34, sm: 44, md: 68 },
                lineHeight: 1.02,
                letterSpacing: "-0.03em",
                mb: 2,
                textShadow: "0 10px 35px rgba(15,23,42,0.34)",
                fontFamily:
                  '"Inter","SF Pro Display","Segoe UI","Helvetica Neue",sans-serif',
                textAlign: { xs: "center", md: "left" },
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                color: "rgba(240,248,255,0.94)",
                fontSize: { xs: 16, sm: 18, md: 22 },
                fontWeight: 400,
                lineHeight: 1.75,
                maxWidth: 760,
                mb: 4,
                mx: { xs: "auto", md: 0 },
                textShadow: "0 4px 18px rgba(15,23,42,0.28)",
                fontFamily:
                  '"Inter","SF Pro Text","Segoe UI","Helvetica Neue",sans-serif',
                textAlign: { xs: "center", md: "left" },
              }}
            >
              {subtitle}
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default function MainLandingPage() {
  const navigate = useNavigate();
  const hostCampus = getCampusFromHost(window.location.hostname);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    institution: "",
    role: "",
    subject: "",
    message: "",
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactResult, setContactResult] = useState(null);

  const handleContactChange = (event) => {
    const { name, value } = event.target;
    setContactForm((current) => ({ ...current, [name]: value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactResult(null);
    setContactSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/public/contact`, contactForm);
      setContactResult({ type: "success", message: data?.message || "Thank you. Your request has been received." });
      setContactForm({ name: "", email: "", phone: "", institution: "", role: "", subject: "", message: "" });
    } catch (error) {
      setContactResult({ type: "error", message: error?.response?.data?.error || "Unable to send your request right now. Please try again." });
    } finally {
      setContactSubmitting(false);
    }
  };

  if (hostCampus) {
    return <CampusLandingPage campusCode={hostCampus} />;
  }

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const brandBlue = "#0B63CE";
  const brandDark = "#071A33";
  const brandOrange = "#F07828";
  const softBlue = "#F3F8FF";

  const features = [
    ["Admissions & Enrollment", "Applications, applicant workflows, student onboarding and admissions operations."],
    ["Student Information System", "Centralized student profiles, documents, IDs, academic records and lifecycle management."],
    ["Academics & Classes", "Academic structures, subjects, classes, schedules, semesters and faculty assignments."],
    ["Attendance", "Student attendance capture, reports, updates, summaries and proactive attendance insights."],
    ["Exams, Marks & Results", "Internal/external marks, grading, subject mapping, results and academic performance tracking."],
    ["Fees, Billing & Payments", "Fee collection, online/cash payments, payment gateways, receipts and financial workflows."],
    ["Digital Campus Wallet", "Campus wallet, POS, devices, gates, bonus rules and controlled digital transactions."],
    ["AI & Predictive Insights", "AI-assisted analytics that surface attendance risk, backlogs, pending actions and priority dashboards."],
    ["AI Chatbot", "A campus-aware assistant for faster answers, guided workflows and access to institutional services."],
    ["Live Classroom", "Interactive classes with whiteboard, questions, screen sharing, participation tools and QR access."],
    ["Feedback & Surveys", "Build feedback forms, generate questions with AI, collect responses and analyze outcomes."],
    ["Time Off & Digital Gate Pass", "Student leave requests, approvals, QR verification, guard scanning and entry/exit records."],
    ["Transport Management", "Buses, routes, stops and transport operations from a unified campus platform."],
    ["Hostel Management", "Hostel rooms, allocations and residential campus operations."],
    ["Career, Jobs & Internships", "Career profiles, achievements, verification, job opportunities, internships and readiness tracking."],
    ["Campus Events & Announcements", "Publish events, announcements, flyers and targeted campus communications."],
    ["Notifications", "Configurable notification providers and campus communication workflows."],
    ["Asset Management", "Track institutional assets and support operational accountability across departments."],
    ["Digital Student ID", "Mobile-ready student identity experiences including Apple Wallet pass support."],
    ["Role-Based Administration", "Purpose-built experiences for administrators, deans, HODs, teachers, students and operational teams."],
    ["Analytics & Dashboards", "Actionable dashboards, operational KPIs, exports and decision-support analytics."],
  ];

  const roles = [
    "Super Admin", "Campus Admin", "Dean", "HOD", "Class Teacher", "Teacher",
    "Class Representative", "Student", "Admissions", "Accounts", "Hostel", "Transport"
  ];

  const screenshots = [
    { title: "Executive Campus Dashboard", subtitle: "Real-time operational intelligence", image: "/screenshots/unifloo-dashboard.png" },
    { title: "Student Mobile Experience", subtitle: "Campus services from anywhere", image: "/screenshots/unifloo-mobile.png" },
    { title: "AI-Powered Analytics", subtitle: "Turn campus data into action", image: "/screenshots/unifloo-ai-analytics.png" },
  ];

  const BrandMark = ({ size = 44, app = false }) => (
    <Box
      component="img"
      src="/unifloo-icon.png"
      alt="UniFLOO"
      sx={{
        width: size,
        height: size,
        borderRadius: app ? `${Math.round(size * 0.23)}px` : `${Math.round(size * 0.2)}px`,
        objectFit: "cover",
        display: "block",
        flexShrink: 0,
        boxShadow: app ? "0 10px 28px rgba(11,99,206,.20)" : "none",
      }}
    />
  );

  const SectionHeading = ({ eyebrow, title, body, light = false }) => (
    <Box sx={{ maxWidth: 780, mx: "auto", textAlign: "center", mb: { xs: 5, md: 7 } }}>
      <Typography sx={{ color: light ? "#7CC4FF" : brandBlue, fontWeight: 900, fontSize: 13, letterSpacing: 1.5, textTransform: "uppercase", mb: 1.2 }}>
        {eyebrow}
      </Typography>
      <Typography sx={{ color: light ? "#fff" : brandDark, fontWeight: 900, fontSize: { xs: 30, md: 44 }, lineHeight: 1.12, letterSpacing: "-.025em", mb: 2 }}>
        {title}
      </Typography>
      {body && <Typography sx={{ color: light ? "rgba(255,255,255,.72)" : "#5D6B7E", fontSize: { xs: 16, md: 18 }, lineHeight: 1.75 }}>{body}</Typography>}
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff", color: brandDark, fontFamily: '"Inter","SF Pro Display","Segoe UI",sans-serif' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: "rgba(255,255,255,.94)", color: brandDark, backdropFilter: "blur(18px)", borderBottom: "1px solid #E7EDF5" }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: 72, gap: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.3} sx={{ flexGrow: 1, cursor: "pointer" }} onClick={() => scrollTo("home")}>
              <BrandMark size={42} app />
              <Box>
                <Typography sx={{ fontWeight: 950, fontSize: 22, lineHeight: 1 }}>UniFLOO</Typography>
                <Typography sx={{ color: "#738197", fontSize: 10.5, fontWeight: 800, letterSpacing: .8, mt: .35 }}>DIGITAL CAMPUS</Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={.4} sx={{ display: { xs: "none", lg: "flex" } }}>
              {[["Platform","platform"],["Solutions","solutions"],["AI","ai"],["Screenshots","screenshots"],["Mobile App","mobile"],["Support","support"],["Contact","contact"]].map(([label,id]) => (
                <Button key={id} color="inherit" onClick={() => scrollTo(id)} sx={{ textTransform: "none", fontWeight: 750, px: 1.25 }}>{label}</Button>
              ))}
            </Stack>
            <Button variant="outlined" onClick={() => scrollTo("contact")} sx={{ display: { xs: "none", sm: "inline-flex" }, borderRadius: 2.5, textTransform: "none", fontWeight: 850 }}>Request Demo</Button>
            <LoginFlyout buttonVariant="contained" />
          </Toolbar>
        </Container>
      </AppBar>

      <Box id="home" sx={{ position: "relative", overflow: "hidden", background: "linear-gradient(180deg,#F7FBFF 0%,#FFFFFF 100%)", pt: { xs: 7, md: 10 }, pb: { xs: 8, md: 12 } }}>
        <Box sx={{ position: "absolute", width: 620, height: 620, borderRadius: "50%", bgcolor: "rgba(22,140,235,.08)", top: -310, right: -180 }} />
        <Container maxWidth="xl" sx={{ position: "relative" }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6.5}>
              <Chip label="AI-Powered • Enterprise-Ready • Multi-Campus" sx={{ bgcolor: "#E9F4FF", color: brandBlue, fontWeight: 850, mb: 2.5 }} />
              <Typography sx={{ fontWeight: 950, fontSize: { xs: 42, sm: 56, md: 70 }, lineHeight: .99, letterSpacing: "-.045em", maxWidth: 820 }}>
                One intelligent platform for your <Box component="span" sx={{ color: brandBlue }}>entire digital campus.</Box>
              </Typography>
              <Typography sx={{ color: "#58677A", fontSize: { xs: 17, md: 20 }, lineHeight: 1.75, maxWidth: 760, mt: 3, mb: 4 }}>
                UniFLOO unifies admissions, academics, attendance, fees, exams, student services, hostel, transport, careers, live classrooms, campus wallet, analytics and AI in one secure enterprise platform.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button variant="contained" size="large" onClick={() => scrollTo("contact")} sx={{ px: 3.5, py: 1.45, borderRadius: 2.5, textTransform: "none", fontWeight: 900, boxShadow: "0 12px 28px rgba(11,99,206,.24)" }}>Request a Demo</Button>
                <Button variant="outlined" size="large" onClick={() => scrollTo("screenshots")} sx={{ px: 3.5, py: 1.45, borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}>Explore the Platform</Button>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 3.5 }}>
                {["Web + Mobile", "Role-based access", "Campus-aware", "API-first", "Custom branding"].map((x) => <Chip key={x} label={x} variant="outlined" sx={{ fontWeight: 700, bgcolor: "#fff" }} />)}
              </Stack>
            </Grid>

            <Grid item xs={12} md={5.5}>
              <Box sx={{ p: { xs: 1.2, md: 1.6 }, borderRadius: 5, bgcolor: "#fff", border: "1px solid #E2EBF5", boxShadow: "0 30px 80px rgba(24,55,92,.18)", transform: { md: "perspective(1200px) rotateY(-3deg) rotateX(1deg)" } }}>
                <Box sx={{ borderRadius: 3.5, overflow: "hidden", bgcolor: "#F6F9FC" }}>
                  <Stack direction="row" alignItems="center" spacing={.7} sx={{ px: 2, py: 1.3, bgcolor: brandDark }}>
                    {[0,1,2].map((n) => <Box key={n} sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: n === 0 ? brandOrange : "rgba(255,255,255,.45)" }} />)}
                    <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: 11, ml: "10px !important" }}>UniFLOO • Campus Command Center</Typography>
                  </Stack>
                  <Box sx={{ p: 2.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Box><Typography sx={{ fontWeight: 900, fontSize: 20 }}>Good morning, Admin</Typography><Typography sx={{ color: "text.secondary", fontSize: 12 }}>Here’s what needs attention today.</Typography></Box>
                      <BrandMark size={38} app />
                    </Stack>
                    <Grid container spacing={1.3}>
                      {[["96.4%","Attendance"],["₹12.4L","Collections"],["72","Approvals"],["18","AI Alerts"]].map(([v,l]) => <Grid item xs={6} key={l}><Box sx={{ p: 1.7, bgcolor: "#fff", border: "1px solid #E7EDF5", borderRadius: 2.5 }}><Typography sx={{ fontWeight: 950, fontSize: 22, color: l === "AI Alerts" ? brandOrange : brandBlue }}>{v}</Typography><Typography sx={{ color: "#6D7B8E", fontSize: 11 }}>{l}</Typography></Box></Grid>)}
                    </Grid>
                    <Box sx={{ mt: 1.5, p: 2, bgcolor: "#fff", border: "1px solid #E7EDF5", borderRadius: 2.5 }}>
                      <Typography sx={{ fontWeight: 850, fontSize: 13, mb: 1.5 }}>AI Campus Insights</Typography>
                      {["Attendance pending in 3 classes", "10 students need academic intervention", "6 approvals are nearing SLA"].map((x,i) => <Stack key={x} direction="row" spacing={1.2} alignItems="center" sx={{ py: .7 }}><Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: i === 0 ? brandOrange : brandBlue }} /><Typography sx={{ fontSize: 11.5, color: "#536174" }}>{x}</Typography></Stack>)}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ borderTop: "1px solid #EDF1F6", borderBottom: "1px solid #EDF1F6", py: 3, bgcolor: "#fff" }}>
        <Container maxWidth="xl"><Stack direction="row" justifyContent="space-around" flexWrap="wrap" gap={2}>{["Admissions to Alumni", "Enterprise Security", "Multi-Campus", "AI Integrated", "Web + Mobile", "Fully Customizable"].map(x => <Typography key={x} sx={{ color: "#64748B", fontWeight: 800, fontSize: 13 }}>✓ {x}</Typography>)}</Stack></Container>
      </Box>

      <Box id="platform" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          <SectionHeading eyebrow="Complete Platform" title="Everything your campus needs. One connected system." body="Replace disconnected portals and manual workflows with a campus-wide digital operating system designed for students, faculty, administrators and operational teams." />
          <Grid container spacing={2.2}>
            {features.map(([title, desc], i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={title}>
                <Card elevation={0} sx={{ height: "100%", border: "1px solid #E5ECF4", borderRadius: 3.5, p: 2.5, transition: ".2s ease", "&:hover": { transform: "translateY(-4px)", boxShadow: "0 18px 45px rgba(18,52,86,.10)", borderColor: "#C8DDF5" } }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2.3, display: "grid", placeItems: "center", bgcolor: i % 5 === 0 ? "#FFF2E9" : "#EAF4FF", color: i % 5 === 0 ? brandOrange : brandBlue, fontWeight: 950, mb: 2 }}>{String(i + 1).padStart(2,"0")}</Box>
                  <Typography sx={{ fontWeight: 900, fontSize: 17, mb: 1 }}>{title}</Typography>
                  <Typography sx={{ color: "#68778A", fontSize: 13.5, lineHeight: 1.7 }}>{desc}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="ai" sx={{ py: { xs: 8, md: 12 }, bgcolor: brandDark, color: "#fff", overflow: "hidden", position: "relative" }}>
        <Container maxWidth="xl" sx={{ position: "relative" }}>
          <Grid container spacing={7} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: "#73BEFF", fontWeight: 900, fontSize: 13, letterSpacing: 1.4, textTransform: "uppercase", mb: 1.5 }}>UniFLOO Intelligence</Typography>
              <Typography sx={{ fontWeight: 950, fontSize: { xs: 34, md: 52 }, lineHeight: 1.08, letterSpacing: "-.03em", mb: 2.5 }}>Move from reactive administration to a preventive digital campus.</Typography>
              <Typography sx={{ color: "rgba(255,255,255,.72)", fontSize: 17, lineHeight: 1.8, mb: 3 }}>UniFLOO can bring operational signals together so leadership can identify missing attendance, low attendance, academic backlogs, pending approvals and other priority actions before they become bigger problems.</Typography>
              <Stack spacing={1.3}>{["AI-assisted campus dashboards", "Priority student and operational alerts", "Campus-aware conversational assistant", "Feedback question generation", "Decision-ready analytics across modules"].map(x => <Typography key={x} sx={{ fontWeight: 750, color: "rgba(255,255,255,.9)" }}>✓&nbsp;&nbsp;{x}</Typography>)}</Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, bgcolor: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.12)", backdropFilter: "blur(12px)" }}>
                <Typography sx={{ fontWeight: 900, mb: 2 }}>Today’s recommended actions</Typography>
                {[["High priority","Attendance not recorded","3 classes require action"],["Academic risk","Students with multiple backlogs","Review top 10 intervention list"],["Engagement","Low attendance trend","12 students below threshold"],["Operations","Pending approvals","6 items nearing SLA"]].map(([tag,title,body],i) => <Box key={title} sx={{ p: 2, mb: i===3?0:1.2, borderRadius: 2.5, bgcolor: "rgba(255,255,255,.07)" }}><Stack direction="row" justifyContent="space-between" gap={2}><Box><Typography sx={{ fontWeight: 850, fontSize: 14 }}>{title}</Typography><Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 12, mt: .3 }}>{body}</Typography></Box><Chip label={tag} size="small" sx={{ bgcolor: i===0 ? "#FFF0E5" : "#E8F4FF", color: i===0 ? "#C85B12" : brandBlue, fontWeight: 850 }} /></Stack></Box>)}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box id="solutions" sx={{ py: { xs: 8, md: 12 }, bgcolor: softBlue }}>
        <Container maxWidth="xl">
          <SectionHeading eyebrow="Built for every role" title="One platform. Purpose-built experiences." body="Give each campus role the tools and information they need while maintaining centralized governance and campus-aware access." />
          <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1.2}>{roles.map(r => <Chip key={r} label={r} sx={{ px: 1.2, py: 2.2, bgcolor: "#fff", border: "1px solid #DDE8F5", fontWeight: 800, fontSize: 13 }} />)}</Stack>
        </Container>
      </Box>

      <Box id="screenshots" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          <SectionHeading eyebrow="Product Experience" title="A modern experience across web and mobile." body="Use this showcase for your production screenshots. The cards gracefully fall back to polished preview panels until your screenshot files are added." />
          <Grid container spacing={3}>
            {screenshots.map((shot, i) => (
              <Grid item xs={12} md={i === 0 ? 12 : 6} key={shot.title}>
                <Box sx={{ border: "1px solid #DFE8F2", borderRadius: 4, overflow: "hidden", boxShadow: "0 18px 55px rgba(18,52,86,.09)", bgcolor: "#fff" }}>
                  <Box sx={{ p: 1.2, bgcolor: "#F5F8FC", borderBottom: "1px solid #E4EAF2" }}><Stack direction="row" spacing={.6}>{[1,2,3].map(n => <Box key={n} sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: n===1 ? brandOrange : "#C9D4E1" }} />)}</Stack></Box>
                  <Box sx={{ height: { xs: 250, md: i===0 ? 500 : 380 }, position: "relative", bgcolor: i===1 ? "#0D2A4C" : "#F4F8FD", display: "grid", placeItems: "center", overflow: "hidden" }}>
                    <Box component="img" src={shot.image} alt={shot.title} onError={(e) => { e.currentTarget.style.display = "none"; }} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                    <Box sx={{ textAlign: "center", px: 3, color: i===1 ? "#fff" : brandDark }}><BrandMark size={64} app /><Typography sx={{ fontWeight: 950, fontSize: { xs: 24, md: 30 }, mt: 2 }}>{shot.title}</Typography><Typography sx={{ opacity: .68, mt: .7 }}>{shot.subtitle}</Typography><Typography sx={{ opacity: .45, fontSize: 11, mt: 2 }}>{shot.image}</Typography></Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="mobile" sx={{ py: { xs: 8, md: 12 }, background: "linear-gradient(135deg,#EAF5FF,#FFF7F0)" }}>
        <Container maxWidth="lg">
          <Grid container spacing={7} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}><BrandMark size={82} app /><Box><Typography sx={{ fontWeight: 950, fontSize: 30 }}>UniFLOO Mobile</Typography><Typography sx={{ color: "#64748B" }}>Your campus in your pocket.</Typography></Box></Stack>
              <Typography sx={{ fontWeight: 950, fontSize: { xs: 34, md: 48 }, lineHeight: 1.1, mb: 2 }}>One connected mobile experience for students and campus teams.</Typography>
              <Typography sx={{ color: "#5F6E81", fontSize: 17, lineHeight: 1.8, mb: 3 }}>Access attendance, time off, digital ID, wallet, feedback, career services, jobs, live classrooms, notifications and more through a role-aware mobile experience.</Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap><Button variant="contained" sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}>iOS App</Button><Button variant="outlined" sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}>Android App</Button></Stack>
            </Grid>
            <Grid item xs={12} md={6}><Box sx={{ maxWidth: 340, mx: "auto", p: 1.2, borderRadius: 7, bgcolor: "#111827", boxShadow: "0 30px 70px rgba(17,24,39,.25)" }}><Box sx={{ minHeight: 570, borderRadius: 6, bgcolor: "#F8FAFC", p: 2.3 }}><Box sx={{ width: 90, height: 20, bgcolor: "#111827", borderRadius: 20, mx: "auto", mb: 3 }} /><BrandMark size={58} app /><Typography sx={{ fontWeight: 950, fontSize: 23, mt: 2 }}>Welcome to UniFLOO</Typography><Typography sx={{ color: "#64748B", fontSize: 12, mb: 2.5 }}>Everything you need for campus life.</Typography><Grid container spacing={1.2}>{["Attendance","Wallet","Time Off","Career","Feedback","Live Class","ID Card","More"].map(x => <Grid item xs={6} key={x}><Box sx={{ bgcolor: "#fff", border: "1px solid #E5EAF1", borderRadius: 2.5, p: 1.5 }}><Box sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: "#EAF4FF", mb: 1 }} /><Typography sx={{ fontSize: 11, fontWeight: 800 }}>{x}</Typography></Box></Grid>)}</Grid></Box></Box></Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 11 } }}>
        <Container maxWidth="xl"><Grid container spacing={3}>{[["Enterprise security","Role-based access, campus-aware data controls and secure authentication patterns."],["Custom for your institution","Campus branding, configurable workflows and modular experiences built around your operations."],["API-first architecture","Connect campus workflows, mobile experiences and external services through a modern API-driven platform."],["Operational visibility","Bring academic, financial, student-service and campus-operation signals into actionable dashboards."]].map(([t,d]) => <Grid item xs={12} sm={6} md={3} key={t}><Box sx={{ p: 3, height: "100%", borderLeft: `3px solid ${brandBlue}` }}><Typography sx={{ fontWeight: 900, fontSize: 18, mb: 1 }}>{t}</Typography><Typography sx={{ color: "#68778A", lineHeight: 1.7, fontSize: 13.5 }}>{d}</Typography></Box></Grid>)}</Grid></Container>
      </Box>

      <Box id="support" sx={{ py: { xs: 8, md: 10 }, bgcolor: "#F7F9FC" }}>
        <Container maxWidth="lg">
          <SectionHeading eyebrow="Support" title="Support for your campus teams." body="Give administrators and users clear paths for product help, onboarding assistance and issue resolution." />
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography sx={{ color: "#64748B", fontSize: 15 }}>
              Need help? Email us at{" "}
              <Box component="a" href="mailto:support@aiorbitapps.com" sx={{ color: brandBlue, fontWeight: 900, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                support@aiorbitapps.com
              </Box>
            </Typography>
          </Box>
          <Grid container spacing={2.5}>{[["Help Center","Guides, onboarding resources and product documentation."],["Technical Support","Get help with platform access, configuration and technical issues."],["Implementation & Training","Structured rollout support for administrators and campus teams."]].map(([t,d]) => <Grid item xs={12} md={4} key={t}><Card elevation={0} sx={{ p: 3, height: "100%", border: "1px solid #E3EAF2", borderRadius: 3.5 }}><Typography sx={{ fontWeight: 900, fontSize: 19, mb: 1 }}>{t}</Typography><Typography sx={{ color: "#69788A", lineHeight: 1.7, mb: 2 }}>{d}</Typography><Button onClick={() => scrollTo("contact")} sx={{ p: 0, textTransform: "none", fontWeight: 850 }}>Contact support →</Button></Card></Grid>)}</Grid>
        </Container>
      </Box>

      <Box id="contact" sx={{ py: { xs: 8, md: 12 }, bgcolor: brandBlue }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: "#fff", fontWeight: 950, fontSize: { xs: 36, md: 52 }, lineHeight: 1.08, mb: 2 }}>Ready to build a smarter digital campus?</Typography>
              <Typography sx={{ color: "rgba(255,255,255,.78)", fontSize: 18, lineHeight: 1.7, maxWidth: 650 }}>Talk to us about your institution, modules, integrations, mobile rollout and custom workflows. Submit the form and our team can follow up with you.</Typography>
              <Stack spacing={1.1} sx={{ mt: 3 }}>
                {["Product demonstrations and solution discovery", "Implementation, integrations and migration", "Technical support and campus onboarding"].map((item) => (
                  <Typography key={item} sx={{ color: "rgba(255,255,255,.92)", fontWeight: 750 }}>✓&nbsp;&nbsp;{item}</Typography>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card component="form" onSubmit={handleContactSubmit} elevation={0} sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 4 }}>
                <Typography sx={{ fontWeight: 950, fontSize: 24, mb: .7 }}>Contact UniFLOO</Typography>
                <Typography sx={{ color: "#6B7788", mb: .7 }}>Sales, demos, implementation and support.</Typography>
                <Typography sx={{ color: "#6B7788", fontSize: 13.5, mb: 2.5 }}>
                  Support: <Box component="a" href="mailto:support@aiorbitapps.com" sx={{ color: brandBlue, fontWeight: 850, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>support@aiorbitapps.com</Box>
                </Typography>
                {contactResult && <Alert severity={contactResult.type} sx={{ mb: 2 }}>{contactResult.message}</Alert>}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField fullWidth required label="Name" name="name" value={contactForm.name} onChange={handleContactChange} inputProps={{ maxLength: 150 }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth required type="email" label="Work Email" name="email" value={contactForm.email} onChange={handleContactChange} inputProps={{ maxLength: 190 }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" name="phone" value={contactForm.phone} onChange={handleContactChange} inputProps={{ maxLength: 40 }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Institution" name="institution" value={contactForm.institution} onChange={handleContactChange} inputProps={{ maxLength: 190 }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Role / Designation" name="role" value={contactForm.role} onChange={handleContactChange} inputProps={{ maxLength: 120 }} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Subject" name="subject" value={contactForm.subject} onChange={handleContactChange} inputProps={{ maxLength: 190 }} /></Grid>
                  <Grid item xs={12}><TextField fullWidth required multiline minRows={4} label="How can we help?" name="message" value={contactForm.message} onChange={handleContactChange} inputProps={{ maxLength: 5000 }} /></Grid>
                </Grid>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2.5 }}>
                  <Button type="submit" disabled={contactSubmitting} variant="contained" size="large" sx={{ flex: 1, textTransform: "none", fontWeight: 900, borderRadius: 2.5 }}>
                    {contactSubmitting ? "Sending..." : "Send Request"}
                  </Button>
                  <Button variant="outlined" size="large" onClick={() => navigate("/login")} sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2.5 }}>Customer Login</Button>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="footer" sx={{ bgcolor: "#061426", color: "#fff", pt: 7, pb: 3 }}>
        <Container maxWidth="xl">
          <Grid container spacing={4} sx={{ pb: 5 }}>
            <Grid item xs={12} md={5}><Stack direction="row" spacing={1.4} alignItems="center"><BrandMark size={48} app /><Box><Typography sx={{ fontWeight: 950, fontSize: 23 }}>UniFLOO</Typography><Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: 11, letterSpacing: 1 }}>ENTERPRISE DIGITAL CAMPUS</Typography></Box></Stack><Typography sx={{ color: "rgba(255,255,255,.58)", mt: 2, maxWidth: 440, lineHeight: 1.7 }}>A unified, AI-powered campus management platform for institutions that want connected operations, better experiences and actionable intelligence.</Typography></Grid>
            <Grid item xs={6} sm={4} md={2}><Typography sx={{ fontWeight: 900, mb: 1.5 }}>Platform</Typography>{["Features","AI","Screenshots","Mobile App"].map((x,i) => <Button key={x} onClick={() => scrollTo(["platform","ai","screenshots","mobile"][i])} sx={{ display: "block", color: "rgba(255,255,255,.58)", textTransform: "none", p: .3 }}>{x}</Button>)}</Grid>
            <Grid item xs={6} sm={4} md={2}><Typography sx={{ fontWeight: 900, mb: 1.5 }}>Company</Typography>{["Contact Us","Support","Customer Login"].map((x,i) => <Button key={x} onClick={() => i===2 ? navigate("/login") : scrollTo(i===0?"contact":"support")} sx={{ display: "block", color: "rgba(255,255,255,.58)", textTransform: "none", p: .3 }}>{x}</Button>)}</Grid>
            <Grid item xs={12} sm={4} md={3}><Typography sx={{ fontWeight: 900, mb: 1.5 }}>Built for modern campuses</Typography><Typography sx={{ color: "rgba(255,255,255,.58)", lineHeight: 1.8, fontSize: 13 }}>Cloud-ready • Multi-campus • Web + Mobile<br/>Customizable • API-first • AI integrated</Typography><Typography component="a" href="mailto:support@aiorbitapps.com" sx={{ display: "inline-block", color: "rgba(255,255,255,.78)", fontWeight: 800, fontSize: 13, mt: 1.5, textDecoration: "none", "&:hover": { color: "#fff", textDecoration: "underline" } }}>support@aiorbitapps.com</Typography></Grid>
          </Grid>
          <Box sx={{ borderTop: "1px solid rgba(255,255,255,.1)", pt: 2.5 }}><Typography sx={{ color: "rgba(255,255,255,.42)", fontSize: 12 }}>© {new Date().getFullYear()} UniFLOO by AI Orbit Apps. All rights reserved.</Typography></Box>
        </Container>
      </Box>
    </Box>
  );
}
